import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
  VALIDATION_MESSAGE,
} from 'src/app/components/shared/shared-constants';
import { InputType } from 'src/app/core/enums';
import { LATITUDE_LONGITUDE } from 'src/app/core/enums/lat-long.enum';
import { SpecialCharacters } from 'src/app/core/enums/special-characters.enum';
import { LocationProfile, RouteAdmin } from 'src/app/core/models/admin';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { LocationValues } from '../../route-admin.enum';
import {
  AddEditRoutePopupData,
  AddRouteIdoDataType,
  DISPLAY_CONSTS,
  DROPDOWN_VALUES,
  FORM_CONTROL_DATA,
} from './add-route-ido.constants';

@Component({
  selector: 'app-add-route-ido',
  templateUrl: './add-route-ido.component.html',
  styleUrls: ['./add-route-ido.component.scss'],
  standalone: false,
})
export class AddRouteIdoComponent implements OnInit {
  public readonly commonUtils = inject(CommonUtils);
  public data: AddEditRoutePopupData = inject(MAT_DIALOG_DATA);
  private readonly destroyRef = inject(DestroyRef);
  private dialogRef = inject(MatDialogRef<AddRouteIdoComponent>);
  private fb = inject(FormBuilder);
  private locationService = inject(LocationService);

  readonly CONSTANTS = { ...DISPLAY_CONSTS, ...VALIDATION_MESSAGE };
  readonly IMAGES = IMAGE_SRC;
  readonly IMAGE_OPTIONS = STATUS_OPTIONS;
  readonly FORM_CONTROL = FORM_CONTROL_DATA;
  readonly InputType = InputType;
  readonly DROP_DOWN_VALUES = DROPDOWN_VALUES;
  readonly VEHICLE_VALUES = DROPDOWN_VALUES.vehicleDropdownOptions
    .slice()
    .sort((a, b) => a.label.localeCompare(b.label));
  readonly ERROR_CONSTANTS = VALIDATION_MESSAGE;
  readonly SpecialCharacters = SpecialCharacters;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  hasBreak = true;
  isEdit = false;
  hasInvalidBreakTime = false;

  currLocation!: LocationProfile;
  addRouteForm!: FormGroup;
  isAddressFieldsReadOnly = false;

  ngOnInit(): void {
    this.isEdit = this.data.isEdit;
    let formControls = {};

    Object.values(FORM_CONTROL_DATA).forEach((value) => {
      const defaultValue =
        value.inputType === InputType.select ? value.defaultValue : '';
      formControls = {
        ...formControls,
        [value.formControlName]: [defaultValue, value.validators],
      };
    });

    this.addRouteForm = this.fb.group(formControls);

    if (this.isEdit) {
      this.addRouteForm
        .get(FORM_CONTROL_DATA.LAT_START_END.formControlName)
        ?.addValidators(
          this.commonUtils.latLongValidator(LATITUDE_LONGITUDE.LATITUDE),
        );

      this.addRouteForm
        .get(FORM_CONTROL_DATA.LONG_START_END.formControlName)
        ?.addValidators(
          this.commonUtils.latLongValidator(LATITUDE_LONGITUDE.LONGITUDE),
        );
    }

    this.populateRouteFields(
      this.data.data,
      this.data.isEdit,
      this.data.location,
    );

    this.isAddressFieldsReadOnly = !this.data.isEdit && this.isFacilityValue;
    this.getCurrentLocation();
    this.addRouteForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newValue: RouteAdmin) => {
        if (
          newValue.break_start &&
          newValue.break_end &&
          (newValue.break_start > newValue.break_end ||
            newValue.break_start == newValue.break_end)
        ) {
          this.addRouteForm.setErrors({ ['invalidBreakTime']: true });
          this.hasInvalidBreakTime = true;
        } else {
          if (this.hasInvalidBreakTime) {
            this.addRouteForm.setErrors({ ['invalidBreakTime']: null });
            this.hasInvalidBreakTime = false;
          }
        }
      });
  }

  getCurrentLocation() {
    this.locationService
      .getCurrentLocation$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((profile) => {
        if (!this.data?.isEdit) {
          this.currLocation = profile.location as LocationProfile;
          this.setDefaultFormData();
        }
      });
  }

  onSubmit(): void {
    let returnValue;
    if (this.data) {
      returnValue = {
        id: this.data ? this.data.data?.id : '',
        ...this.addRouteForm.getRawValue(),
      };
    } else {
      returnValue = this.addRouteForm.getRawValue();
    }

    this.dialogRef.close(returnValue);
  }

  onClose(): void {
    this.commonUtils.openExitModal(this.addRouteForm.dirty);
  }

  onBreakSelectChange(): void {
    this.hasBreak = this.addRouteForm.get(
      FORM_CONTROL_DATA.BREAK.formControlName,
    )?.value;
    if (this.hasBreak) {
      this.addBreakFields();
    } else {
      this.removeBreakFields();
    }
  }

  dataChanged(): void {
    this.addRouteForm.markAsDirty();
  }

  updateLocation() {
    switch (
      this.addRouteForm.get(
        this.FORM_CONTROL.LOCATION_START_END.formControlName,
      )?.value
    ) {
      case LocationValues.Facility:
        this.isEdit
          ? this.populateEditAddressFields()
          : this.populateAddAddressFields();
        this.isAddressFieldsReadOnly = !this.isEdit;
        break;
      case LocationValues.Other:
        this.addRouteForm
          .get(this.FORM_CONTROL.ADDRESS_START_END.formControlName)
          ?.reset();
        this.addRouteForm
          .get(this.FORM_CONTROL.LAT_START_END.formControlName)
          ?.reset();
        this.addRouteForm
          .get(this.FORM_CONTROL.LONG_START_END.formControlName)
          ?.reset();
        this.isAddressFieldsReadOnly = false;
        break;
      default:
        break;
    }
  }

  get isFacilityValue() {
    const locationStartEnd = this.addRouteForm.get(
      this.FORM_CONTROL.LOCATION_START_END.formControlName,
    )?.value;
    return locationStartEnd === LocationValues.Facility;
  }

  private setDefaultFormData() {
    if (this.isFacilityValue) {
      this.addRouteForm
        .get(FORM_CONTROL_DATA.ADDRESS_START_END.formControlName)
        ?.setValue(this.currLocation?.address?.streetname);
      this.addRouteForm
        .get(FORM_CONTROL_DATA.LAT_START_END.formControlName)
        ?.setValue(this.currLocation?.latitude);
      this.addRouteForm
        .get(FORM_CONTROL_DATA.LONG_START_END.formControlName)
        ?.setValue(this.currLocation?.longitude);
    }

    this.addRouteForm
      .get(FORM_CONTROL_DATA.LBT.formControlName)
      ?.setValue(this.currLocation?.leaveBuildingTime);

    this.addRouteForm
      .get(FORM_CONTROL_DATA.BREAK_START.formControlName)
      ?.setValue(this.currLocation?.breakStart);
    this.addRouteForm
      .get(FORM_CONTROL_DATA.BREAK_END.formControlName)
      ?.setValue(this.currLocation?.breakEnd);
    this.addRouteForm
      .get(FORM_CONTROL_DATA.DURATION.formControlName)
      ?.setValue(this.currLocation?.breakDuration);
    if (this.currLocation?.waitTimeDelivery) {
      this.addRouteForm
        .get(FORM_CONTROL_DATA.DEL_WAIT_TIME.formControlName)
        ?.setValue(this.currLocation?.waitTimeDelivery);
    }
    if (this.currLocation?.waitTimePickup) {
      this.addRouteForm
        .get(FORM_CONTROL_DATA.PU_WAIT_TIME.formControlName)
        ?.setValue(this.currLocation?.waitTimePickup);
    }
  }

  private addBreakFields(): void {
    this.addRouteForm.addControl(
      FORM_CONTROL_DATA.BREAK_START.formControlName,
      new FormControl(
        this.data.location
          ? this.data?.location?.breakStart
          : this.data?.data?.breakStart,
        FORM_CONTROL_DATA.BREAK_START.validators,
      ),
    );
    this.addRouteForm.addControl(
      FORM_CONTROL_DATA.BREAK_END.formControlName,
      new FormControl(
        this.data.location
          ? this.data?.location?.breakEnd
          : this.data?.data?.breakEnd,
        FORM_CONTROL_DATA.BREAK_END.validators,
      ),
    );
    this.addRouteForm.addControl(
      FORM_CONTROL_DATA.DURATION.formControlName,
      new FormControl(
        this.data.location
          ? this.data?.location?.breakDuration
          : this.data?.data?.duration,
        FORM_CONTROL_DATA.DURATION.validators,
      ),
    );
  }

  private removeBreakFields(): void {
    this.addRouteForm.removeControl(
      FORM_CONTROL_DATA.BREAK_START.formControlName,
    );
    this.addRouteForm.removeControl(
      FORM_CONTROL_DATA.BREAK_END.formControlName,
    );
    this.addRouteForm.removeControl(FORM_CONTROL_DATA.DURATION.formControlName);
  }

  private populateRouteFields(
    data: AddRouteIdoDataType,
    isEdit: boolean,
    location: LocationProfile,
  ) {
    if (data) {
      this.populateEditRouteFields();
      this.isEdit = isEdit;
      if (location) {
        this.populateAddRouteFields();
      }
    }
  }

  private populateEditRouteFields() {
    this.addRouteForm.patchValue(this.data.data);
    this.hasBreak = !!(this.data.data ? this.data.data?.break_exist : true);
    this.populateEditAddressFields();
  }

  private populateEditAddressFields() {
    this.addRouteForm
      .get(FORM_CONTROL_DATA.ADDRESS_START_END.formControlName)
      ?.setValue(this.data.data.startend_address);

    this.addRouteForm
      .get(FORM_CONTROL_DATA.LAT_START_END.formControlName)
      ?.setValue(
        this.data.data[FORM_CONTROL_DATA.LAT_START_END.formControlName]?.padEnd(
          13,
          '0',
        ),
      );
    this.addRouteForm
      .get(FORM_CONTROL_DATA.LONG_START_END.formControlName)
      ?.setValue(
        this.data.data[
          FORM_CONTROL_DATA.LONG_START_END.formControlName
        ]?.padEnd(13, '0'),
      );
  }

  private populateAddRouteFields() {
    this.populateAddAddressFields();
    this.addRouteForm
      .get(FORM_CONTROL_DATA.LOCATION_START_END.formControlName)
      ?.setValue(this.data?.location?.startEndLocation);
    this.addRouteForm
      .get(FORM_CONTROL_DATA.VEHICLE.formControlName)
      ?.setValue(this.data?.location?.vehicleType);
    this.addRouteForm
      .get(FORM_CONTROL_DATA.DURATION.formControlName)
      ?.setValue(this.data?.location?.breakDuration);
    this.addRouteForm
      .get(FORM_CONTROL_DATA.BREAK_START.formControlName)
      ?.setValue(this.data?.location?.breakStart);
    this.addRouteForm
      .get(FORM_CONTROL_DATA.BREAK_END.formControlName)
      ?.setValue(this.data?.location?.breakEnd);
    this.addRouteForm
      .get(FORM_CONTROL_DATA.DEL_WAIT_TIME.formControlName)
      ?.setValue(this.data?.location?.waitTimeDelivery);
    this.addRouteForm
      .get(FORM_CONTROL_DATA.PU_WAIT_TIME.formControlName)
      ?.setValue(this.data?.location?.waitTimePickup);
    this.addRouteForm
      .get(FORM_CONTROL_DATA.LBT.formControlName)
      ?.setValue(this.data?.location?.leaveBuildingTime);
    this.addRouteForm
      .get(FORM_CONTROL_DATA.OPTIMIZE.formControlName)
      ?.setValue(this.data?.location?.optimizedDeliveryOrder);
  }

  private populateAddAddressFields() {
    if (!this.data.location) {
      this.setDefaultFormData();
      return;
    }
    this.addRouteForm
      .get(FORM_CONTROL_DATA.LAT_START_END.formControlName)
      ?.setValue(this.data.location.latitude.padEnd(13, '0'));
    this.addRouteForm
      .get(FORM_CONTROL_DATA.LONG_START_END.formControlName)
      ?.setValue(this.data.location.longitude.padEnd(13, '0'));
    this.addRouteForm
      .get(FORM_CONTROL_DATA.ADDRESS_START_END.formControlName)
      ?.setValue(this.data.location?.address?.streetname);
  }
}
